import { makeStyles } from "@material-ui/core/styles";
import { COPY } from "../constants";
import { rv } from "../tokens";
import { CheckIcon } from "./icons";

export interface StepItem {
  index: number;
  label: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    // Solid brand green to match the rail/drawer + footer (no gradient).
    background: rv.green,
    color: rv.onDark,
    // Fill the rail below the ~170px square logo without overflowing (which
    // would push the bottom card off-screen).
    padding: theme.spacing(1, 2.5, 3),
    minHeight: "calc(100vh - 178px)",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  // Steps area: takes the remaining height and scrolls on its own if needed.
  scroll: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  header: {
    marginBottom: theme.spacing(2.5),
  },
  title: {
    fontWeight: 700,
    fontSize: 17,
    margin: 0,
  },
  counter: {
    marginTop: 4,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: rv.green300,
  },
  step: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
    border: "none",
    background: "none",
    color: "inherit",
    textAlign: "left",
    padding: theme.spacing(0.75, 0),
    cursor: "pointer",
    font: "inherit",
  },
  dot: {
    width: 26,
    height: 26,
    flexShrink: 0,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 700,
    background: "rgba(255,255,255,0.16)",
    color: rv.onDark,
  },
  dotCurrent: {
    background: rv.gold,
    color: rv.onDark,
    boxShadow: "0 0 0 4px rgba(200,147,42,0.25)",
  },
  dotComplete: {
    background: "rgba(200,147,42,0.35)",
    color: rv.onDark,
  },
  stepLabel: {
    fontSize: 13.5,
  },
  stepLabelCurrent: {
    fontWeight: 700,
  },
  stepLabelUpcoming: {
    color: "rgba(255,255,255,0.62)",
  },
  // Pinned to the bottom of the rail; never part of the scroll region.
  next: {
    flexShrink: 0,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.14)",
  },
  nextTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: rv.green300,
    margin: 0,
  },
  nextBody: {
    fontSize: 12.5,
    lineHeight: 1.55,
    margin: theme.spacing(1, 0, 0),
    color: "rgba(255,255,255,0.86)",
  },
}));

interface Props {
  steps: StepItem[];
  currentIndex: number;
  onJump: (index: number) => void;
}

export default function StepTracker({ steps, currentIndex, onJump }: Props) {
  const classes = useStyles();
  const currentPos = steps.findIndex((s) => s.index === currentIndex);

  return (
    <nav className={classes.root} aria-label="Update progress">
      <div className={classes.scroll}>
        <div className={classes.header}>
          <p className={classes.title}>{COPY.sidebarTitle}</p>
          <p className={classes.counter}>
            Step {currentPos + 1} of {steps.length}
          </p>
        </div>

        {steps.map((step, position) => {
          const complete = position < currentPos;
          const current = position === currentPos;
          return (
            <button
              type="button"
              key={step.index}
              className={classes.step}
              onClick={() => onJump(step.index)}
              aria-current={current ? "step" : undefined}
            >
              <span
                className={`${classes.dot} ${
                  current ? classes.dotCurrent : complete ? classes.dotComplete : ""
                }`}
              >
                {complete ? <CheckIcon size={15} /> : position + 1}
              </span>
              <span
                className={`${classes.stepLabel} ${
                  current
                    ? classes.stepLabelCurrent
                    : complete
                    ? ""
                    : classes.stepLabelUpcoming
                }`}
              >
                {step.label}
              </span>
            </button>
          );
        })}
      </div>

      <div className={classes.next}>
        <p className={classes.nextTitle}>{COPY.whatHappensNextTitle}</p>
        <p className={classes.nextBody}>{COPY.whatHappensNext}</p>
      </div>
    </nav>
  );
}
